import React, { useMemo, useState } from "react";
import { BsFillArrowLeftCircleFill, BsChevronDown } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleJobsheet,
  toggleOrders,
  toggleQuote,
  toggleStore,
  toggleDocs,
  toggleDiecut,
  toggleProd,
  toggleUser,
} from "../features/PageSlice";

import logo from "../logo.png";
import { NavLink } from "react-router-dom";
import LoginStatus from "../components/LoginStatus";
import { MENU } from "../pages/menuItems"; // ⬅️ import menu

/** map menu key -> redux toggle info */
const SECTION_TOGGLES = {
  quotation: { openFlag: "subQuote", action: toggleQuote },
  jobsheet: { openFlag: "subJobsheet", action: toggleJobsheet },
  orders: { openFlag: "subOrders", action: toggleOrders },
  documents: { openFlag: "subDocs", action: toggleDocs },
  diecut: { openFlag: "subDiecut", action: toggleDiecut },
  production: { openFlag: "subProd", action: toggleProd },
  store: { openFlag: "subStore", action: toggleStore },
  user: { openFlag: "subUser", action: toggleUser },
};

const canSee = (roles, user) => {
  if (!user) return false;
  return !roles || roles.includes(user.user_lvl);
};

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const pageState = useSelector((state) => state.pages);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const visibleMenu = useMemo(
    () => MENU.filter((m) => canSee(m.roles, user)),
    [user]
  );

  return (
    <div className="flex flex-col min-h-screen">
      {/* Top bar */}
      <div className="bg-gray-900 text-white p-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <img
            src={logo}
            width="80"
            height="80"
            alt="logo"
            className="cursor-pointer block duration-500"
          />
          <p className="text-white font-medium whitespace-nowrap">
            Elkom Printama Batam
          </p>
        </div>
        <LoginStatus />
      </div>

      {/* Body */}
      <div className="sm:flex max_sm:text-cyan-700 w-full flex-1">
        {/* Sidebar */}
        <div
          className={`bg-gray-800 px-5 ${
            sidebarOpen ? "sm:w-1/6 max_sm:w-full" : "w-20"
          } duration-100 relative`}
        >
          <BsFillArrowLeftCircleFill
            className={`bg-white text-3xl rounded-full absolute -right-3 top-9 border cursor-pointer ${
              !sidebarOpen && "rotate-180"
            }`}
            onClick={() => setSidebarOpen((v) => !v)}
          />

          <ul className="pt-2 max_sm:text-center">
            {visibleMenu.map((item) => {
              const Icon = item.icon;
              const hasChildren =
                Array.isArray(item.children) && item.children.length > 0;
              const toggleInfo = SECTION_TOGGLES[item.key];
              const isOpen = toggleInfo
                ? pageState[toggleInfo.openFlag]
                : false;

              return (
                <li key={item.key} className="text-gray-300 text-sm">
                  {/* Parent */}
                  {/* Parent: if no children, make it a NavLink; if children, make it a div */}
                  {!hasChildren && item.to ? (
                    <NavLink
                      to={item.to}
                      className={`flex items-center gap-x-4 p-2 hover:bg-light-white rounded-md mt-2`}
                    >
                      {Icon && <Icon size={"2em"} />}
                      <span
                        className={`pl-5 ${
                          !sidebarOpen && "hidden"
                        } duration-200`}
                      >
                        {item.label}
                      </span>
                    </NavLink>
                  ) : (
                    <div
                      className={`flex items-center gap-x-4 cursor-pointer p-2 hover:bg-light-white rounded-md mt-2 select-none`}
                      onClick={() => {
                        if (toggleInfo) dispatch(toggleInfo.action());
                      }}
                    >
                      {Icon && <Icon size={"2em"} />}
                      <span
                        className={`pl-5 ${
                          !sidebarOpen && "hidden"
                        } duration-200`}
                      >
                        {item.label}
                      </span>
                      <BsChevronDown
                        className={`ml-auto ${!sidebarOpen && "hidden"} ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (toggleInfo) dispatch(toggleInfo.action());
                        }}
                      />
                    </div>
                  )}

                  {/* Children */}
                  {hasChildren && isOpen && (
                    <ul className={`${!sidebarOpen && "hidden"}`}>
                      {item.children
                        .filter((ch) => canSee(ch.roles, user))
                        .map((ch) => (
                          <li
                            key={ch.key}
                            className="text-gray-300 text-sm flex items-center gap-x-4 px-5 cursor-pointer p-2 hover:bg-light-white rounded-md mt-2"
                          >
                            <NavLink
                              className="items-center flex flex-1 flex-row"
                              to={ch.to}
                            >
                              {ch.label}
                            </NavLink>
                          </li>
                        ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Content */}
        <div className="px-5 pt-2 bg-gray-700 overflow-x-auto w-full">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
